/**
 * Duration-based uptime for status.frogmaster.xyz.
 *
 * Formula (per service):
 *   uptime% = 1 - (msDown + 0.5 * msDegraded) / msWindow
 *
 * Down intervals count fully. Degraded intervals count at 50% weight.
 * Overall = unweighted average of per-service uptimes for monitored
 * (non-disabled) services in the same window.
 *
 * Example: 1 hour Down in 7 days ≈ 99.4%.
 */

export const DEGRADED_DOWNTIME_WEIGHT = 0.5;

export const UPTIME_WINDOWS = [
  { id: 'h24', label: '24h', ms: 24 * 60 * 60 * 1000 },
  { id: 'd7', label: '7d', ms: 7 * 24 * 60 * 60 * 1000 },
  { id: 'd30', label: '30d', ms: 30 * 24 * 60 * 60 * 1000 },
  { id: 'd90', label: '90d', ms: 90 * 24 * 60 * 60 * 1000 },
  { id: 'all', label: 'All', ms: null },
];

export function toMillis(value) {
  if (value == null) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value.seconds === 'number') {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
  }
  if (typeof value._seconds === 'number') return value._seconds * 1000;
  return null;
}

function rangeOverlapMs(start, end, windowStart, windowEnd) {
  const overlapStart = Math.max(start, windowStart);
  const overlapEnd = Math.min(end, windowEnd);
  return Math.max(0, overlapEnd - overlapStart);
}

export function incidentOverlapMs(incident, windowStart, windowEnd, now) {
  const start = toMillis(incident.startedAt);
  if (start == null) return 0;
  const ended = toMillis(incident.endedAt);
  const end = ended == null ? now : ended;
  return rangeOverlapMs(start, end, windowStart, windowEnd);
}

export function incidentPenaltyParts(incident, windowStart, windowEnd, now) {
  const start = toMillis(incident.startedAt);
  if (start == null) return { downMs: 0, degradedMs: 0 };
  const ended = toMillis(incident.endedAt);
  const end = ended == null ? now : ended;
  const escalated = toMillis(incident.escalatedAt);
  if (incident.severity === 'DOWN' && escalated != null && escalated > start) {
    return {
      degradedMs: rangeOverlapMs(start, escalated, windowStart, windowEnd),
      downMs: rangeOverlapMs(escalated, end, windowStart, windowEnd),
    };
  }
  const ms = rangeOverlapMs(start, end, windowStart, windowEnd);
  if (incident.severity === 'DOWN') return { downMs: ms, degradedMs: 0 };
  if (incident.severity === 'DEGRADED') return { downMs: 0, degradedMs: ms };
  return { downMs: 0, degradedMs: 0 };
}

export function uptimePercent(incidents, windowMs, now, monitoredSinceMs) {
  const windowEnd = now;
  // Rolling windows are always the full 24h/7d/30d/90d (unknown past = up).
  // All-time starts at monitoredSince when we have it.
  const windowStart = windowMs == null
    ? (typeof monitoredSinceMs === 'number' && monitoredSinceMs > 0 ? monitoredSinceMs : now)
    : now - windowMs;
  const duration = windowEnd - windowStart;
  if (duration <= 0) return 100;
  let downMs = 0;
  let degradedMs = 0;
  const list = Array.isArray(incidents) ? incidents : [];
  for (const incident of list) {
    const parts = incidentPenaltyParts(incident, windowStart, windowEnd, now);
    downMs += parts.downMs;
    degradedMs += parts.degradedMs;
  }
  const penalty = downMs + DEGRADED_DOWNTIME_WEIGHT * degradedMs;
  return Math.max(0, Math.min(100, (1 - penalty / duration) * 100));
}

export function averageUptime(values) {
  const nums = (values || []).filter((v) => typeof v === 'number' && !Number.isNaN(v));
  if (!nums.length) return 100;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

export function formatUptimePct(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return '…';
  const n = Math.max(0, Math.min(100, Number(pct)));
  if (n >= 99) return `${n.toFixed(1)}%`;
  return `${n.toFixed(2)}%`;
}

export function formatDuration(ms) {
  if (ms == null || ms < 0 || Number.isNaN(ms)) return '';
  if (ms < 60000) return `${Math.max(1, Math.round(ms / 1000))}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.round((ms % 3600000) / 60000);
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remH = hours % 24;
    return remH ? `${days}d ${remH}h` : `${days}d`;
  }
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}
